import React, { useMemo, useCallback, useState, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  Position,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { DependencyGraphNode, DependencyGraphEdge } from '../../types';
import { getLayoutedElements, LayoutPresets } from '../../utils/layoutUtils';
import { RefreshCw, Layers, ArrowRight, ArrowDown } from 'lucide-react';

interface DependencyGraphViewProps {
  nodes: DependencyGraphNode[];
  edges: DependencyGraphEdge[];
  onRefresh: () => void;
}

export const DependencyGraphView: React.FC<DependencyGraphViewProps> = ({
  nodes: graphNodes,
  edges: graphEdges,
  onRefresh
}) => {
  const [layoutDirection, setLayoutDirection] = useState<'TB' | 'LR'>('TB');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Transform and layout nodes/edges
  const { layoutedNodes, layoutedEdges } = useMemo(() => {
    const projectColors = {
      'active': 'bg-blue-100 border-blue-500',
      'completed': 'bg-green-100 border-green-500',
      'on_hold': 'bg-yellow-100 border-yellow-500',
      'planning': 'bg-gray-100 border-gray-500'
    };

    const statusColors = {
      'completed': 'bg-green-500',
      'in_progress': 'bg-blue-500',
      'blocked': 'bg-red-500',
      'not_started': 'bg-gray-400'
    };

    // Transform data for React Flow
    const reactFlowNodes: Node[] = graphNodes.map((node) => ({
      id: node.id,
      type: 'default',
      position: { x: 0, y: 0 }, // Will be set by layout
      data: {
        label: (
          <div className={`p-3 rounded-lg border-2 min-w-[200px] ${projectColors[node.projectStatus as keyof typeof projectColors] || 'bg-white border-gray-300'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`w-3 h-3 rounded-full ${statusColors[node.status as keyof typeof statusColors]}`} />
              <span className="text-xs text-gray-500 font-medium">
                Level {node.level}
              </span>
            </div>
            <div className="font-semibold text-gray-900 text-sm mb-1 line-clamp-2">
              {node.label}
            </div>
            <div className="text-xs text-gray-600 mb-2">
              {node.projectName}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(node.progress, 100)}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {Math.round(node.progress)}% complete
            </div>
          </div>
        ),
        level: node.level
      }
    }));

    const reactFlowEdges: Edge[] = graphEdges.map((edge) => {
      const edgeColors = {
        'blocks': '#ef4444', // red
        'enables': '#10b981', // green
        'reduces_complexity': '#f59e0b', // yellow
        'accelerates': '#3b82f6', // blue
      };

      const edgeStyles = {
        'blocks': 'solid',
        'enables': 'solid',
        'reduces_complexity': 'dashed',
        'accelerates': 'dotted',
      };

      return {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: 'smoothstep',
        animated: edge.type === 'accelerates' || edge.type === 'enables',
        style: {
          stroke: edgeColors[edge.type],
          strokeWidth: Math.max(1, edge.strength),
          strokeDasharray: edgeStyles[edge.type] === 'dashed' ? '5,5' : 
                          edgeStyles[edge.type] === 'dotted' ? '2,3' : 'none',
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: edgeColors[edge.type],
        },
        label: (
          <div className="bg-white px-2 py-1 rounded-full border text-xs font-medium shadow-sm">
            {edge.type.replace('_', ' ')} ({edge.strength}/5)
          </div>
        ),
        labelStyle: {
          fontSize: '10px',
          fontWeight: '500',
        },
        labelBgPadding: [8, 4],
        labelBgBorderRadius: 4,
      };
    });

    // Apply auto-layout
    const preset = LayoutPresets.DEPENDENCY_GRAPH;
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      reactFlowNodes,
      reactFlowEdges,
      layoutDirection,
      preset.nodeWidth,
      preset.nodeHeight
    );

    return { layoutedNodes, layoutedEdges };
  }, [graphNodes, graphEdges, layoutDirection]);

  // Update nodes and edges when layout changes
  useEffect(() => {
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [layoutedNodes, layoutedEdges, setNodes, setEdges]);

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    console.log('Node clicked:', node);
    // TODO: Open goal detail modal or navigate to goal
  }, []);

  const onEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    console.log('Edge clicked:', edge);
    // TODO: Open dependency edit modal
  }, []);

  const handleLayoutChange = useCallback((direction: 'TB' | 'LR') => {
    setLayoutDirection(direction);
  }, []);

  const handleAutoLayout = useCallback(() => {
    // Trigger re-layout by updating the layout direction state
    setLayoutDirection(prev => prev === 'TB' ? 'LR' : 'TB');
    setTimeout(() => setLayoutDirection(prev => prev === 'TB' ? 'LR' : 'TB'), 100);
  }, []);

  if (graphNodes.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center text-gray-500 py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V9.618a1 1 0 01.553-.894L9 6m0 14l6-3m-6 3V6m6 14l5.447-2.724A1 1 0 0021 16.382V9.618a1 1 0 00-.553-.894L15 6m0 14V6m0 0L9 6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Dependencies Found</h3>
          <p className="text-gray-500 mb-4">
            Create dependencies between goals to see the dependency graph
          </p>
          <button
            onClick={onRefresh}
            className="btn-secondary flex items-center gap-2 mx-auto"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Layout Controls */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Layout:</span>
          </div>
          <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
            <button
              onClick={() => handleLayoutChange('TB')}
              className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                layoutDirection === 'TB'
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ArrowDown className="w-3 h-3" />
              Hierarchical
            </button>
            <button
              onClick={() => handleLayoutChange('LR')}
              className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                layoutDirection === 'LR'
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ArrowRight className="w-3 h-3" />
              Flow
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleAutoLayout}
            className="btn-secondary flex items-center gap-2 text-xs"
          >
            <Layers className="w-3 h-3" />
            Auto-Layout
          </button>
          <button
            onClick={onRefresh}
            className="btn-secondary flex items-center gap-2 text-xs"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="h-[600px] relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          connectionMode={ConnectionMode.Loose}
          fitView
          fitViewOptions={{
            padding: 0.2,
            includeHiddenNodes: false,
          }}
          nodesDraggable={true}
          nodesConnectable={false}
          elementsSelectable={true}
          selectNodesOnDrag={false}
          panOnScroll={true}
          zoomOnScroll={true}
          zoomOnPinch={true}
          panOnScrollSpeed={0.5}
          minZoom={0.2}
          maxZoom={2}
        >
          <Background />
          <Controls />
        </ReactFlow>
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 bg-red-500 rounded" />
            <span className="text-gray-700">Blocks</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 bg-green-500 rounded" />
            <span className="text-gray-700">Enables</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 bg-yellow-500 rounded" style={{ backgroundImage: 'repeating-linear-gradient(to right, #f59e0b 0, #f59e0b 2px, transparent 2px, transparent 4px)' }} />
            <span className="text-gray-700">Reduces Complexity</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 bg-blue-500 rounded" style={{ backgroundImage: 'repeating-linear-gradient(to right, #3b82f6 0, #3b82f6 1px, transparent 1px, transparent 3px)' }} />
            <span className="text-gray-700">Accelerates</span>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <div className="w-3 h-3 bg-blue-500 rounded-full" />
            <span className="text-gray-700">In Progress</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <span className="text-gray-700">Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full" />
            <span className="text-gray-700">Blocked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-400 rounded-full" />
            <span className="text-gray-700">Not Started</span>
          </div>
        </div>
      </div>
    </div>
  );
};